import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, X, Key } from "lucide-react";
import { apiCall } from "@/lib/api";

interface SecurityStatus {
  uses_default_password: boolean;
  tfa_enabled: boolean;
  password_changed_at: string | null;
  should_show_banner: boolean;
}

const DISMISS_KEY = "byteisp_security_banner_dismissed_session";

/**
 * v1.0.31 — Banner amarillo persistente (mientras esté visible) que sugiere
 * cambiar la password default y/o activar 2FA después de la instalación.
 *
 * No bloquea — solo es visual + linkea a /seguridad. Smoke tests siguen
 * funcionando sin problema. Se oculta hasta cerrar sesión al dar X.
 *
 * Si el endpoint no responde (versiones viejas), no muestra nada.
 */
export function SecurityBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  });

  const { data: status } = useQuery<SecurityStatus>({
    queryKey: ["me", "security-status"],
    queryFn: () =>
      apiCall<SecurityStatus>("get", "/api/v1/me/security-status"),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (dismissed || !status?.should_show_banner) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-3 flex items-start gap-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 dark:border-yellow-500/30 dark:bg-yellow-500/10">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400" />
      <div className="flex-1">
        <div className="text-xs font-semibold text-yellow-900 dark:text-yellow-200">
          Seguridad pendiente — completa estos pasos
        </div>
        <ul className="mt-1 space-y-0.5 text-xs text-yellow-800 dark:text-yellow-300/90">
          {status.uses_default_password && (
            <li>· Cambia la contraseña por default de tu usuario.</li>
          )}
          {!status.tfa_enabled && (
            <li>· Activa 2FA para proteger tu cuenta.</li>
          )}
        </ul>
        <div className="mt-2">
          <Link
            to="/seguridad"
            className="inline-flex items-center gap-1 rounded bg-yellow-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-yellow-700"
          >
            <Key size={11} />
            Ir a Mi seguridad
          </Link>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="text-yellow-700 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-200"
        title="Ocultar hasta cerrar sesión"
      >
        <X size={14} />
      </button>
    </div>
  );
}
